import { useFormContext } from "react-hook-form";
import { components, ControlProps } from "react-select";
import { styled } from "~/application/theme";

const StyledControl = styled(components.Control, {
  // Reset
  boxSizing: "border-box",
  display: "flex",
  userSelect: "none",

  // Custom
  position: "relative",
  transition: "$fast",
  outline: "none",
  gap: "$2",
  fontSize: "$md",
  fontWeight: 500,
  flexWrap: "wrap",
  width: "100%",
  minWidth: "150px",
});

const SelectControlRoot = styled("div", {
  // Reset
  boxSizing: "border-box",
  display: "flex",
  userSelect: "none",

  // Custom
  position: "relative",
  transition: "$fast",
  outline: "none",
  gap: "$2",
  fontSize: "$md",
  fontWeight: 500,
  flexWrap: "wrap",
  border: "none",
  borderRadius: "$md",
  backgroundColor: "$neutrals-white",
  color: "$neutrals-darkest",
  fill: "$neutrals-darkest",

  "&::after": {
    content: "",
    pointerEvents: "none",
    position: "absolute",
    margin: 0,
    inset: 0,
    borderColor: "$neutrals-light",
    borderRadius: "inherit",
    borderStyle: "solid",
    borderWidth: "1px",
    transition: "$faster",
  },

  "&:hover": {
    cursor: "pointer",
    color: "$neutrals-black",
    fill: "$neutrals-black",
  },

  "&[data-dirty=true]": {
    backgroundColor: "$error-light",
    "&::after": {
      borderWidth: "2px",
      borderColor: "$error-base",
    },
  },

  "&[data-focused=true]": {
    boxShadow: "$focus-sm",
  },

  "&[data-filled=true]::after": {
    borderWidth: "2px",
    borderColor: "$primary-base",
  },

  "&[data-disabled=true]": {
    pointerEvents: "none",
    "&::after": {
      borderWidth: "1px",
      borderColor: "$neutrals-dark",
    },
  },

  variants: {
    size: {
      xs: {
        [`& > ${StyledControl}`]: {
          padding: "$2 $4",
        },
      },
      sm: {
        [`& > ${StyledControl}`]: {
          padding: "$3 $4",
        },
      },
      md: {
        [`& > ${StyledControl}`]: {
          padding: "$4 $4",
        },
      },
    },
  },

  defaultVariants: {
    size: "sm",
  },
});

export const SelectControl = (props: ControlProps) => {
  const { size, "data-dirty": isDirty } = props.selectProps as Record<string, any>;

  const { isFieldError } = props.selectProps as Record<string, any>;

  const isFilled = !isFieldError && props.hasValue;
  const { watch } = useFormContext();
  const dataCy = watch("dataCy");
  return (
    <SelectControlRoot
      data-cy={dataCy}
      size={size}
      data-dirty={isDirty}
      data-open={props.menuIsOpen}
      data-disabled={props.isDisabled}
      data-focused={props.isFocused}
      data-filled={isFilled}
    >
      <StyledControl {...props} />
    </SelectControlRoot>
  );
};

SelectControl.displayName = "SelectControl";
